import React, { useMemo } from 'react';
import { Card, Empty, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Column } from '@ant-design/charts';
import type { SkuRow } from '@/types';
import { fmtNum, fmtPct, fmtUsd } from '../bq-insights/bqMath';
import { inr, rate, sumOf } from './monetizationMath';
import type { TypedEntry } from './monetizationTabState';
import { BackendDays } from './tableParts';
import { titleWithInfo, totalRow } from './tableHelpers';
import { TabBody } from './TabBody';

const { Text } = Typography;

const columns: ColumnsType<SkuRow> = [
  { title: 'SKU', dataIndex: 'productId', fixed: 'left' },
  { title: 'Carts', dataIndex: 'carts', align: 'right', render: (v) => fmtNum(v) },
  { title: 'Store opened', dataIndex: 'iapDetails', align: 'right', render: (v) => fmtNum(v) },
  { title: 'Purchases (GA4)', dataIndex: 'purchases', align: 'right', render: (v) => fmtNum(v) },
  {
    title: titleWithInfo('Purchases / carts', 'Events, not players: purchases divided by carts'),
    align: 'right',
    render: (_, r) => fmtPct(rate(r.purchases, r.carts)),
  },
  { title: 'Cancels', dataIndex: 'cancels', align: 'right', render: (v) => fmtNum(v) },
  { title: 'Failed', dataIndex: 'failed', align: 'right', render: (v) => fmtNum(v) },
  {
    title: titleWithInfo('Revenue (GA4, gross)', 'GA4 Play price, before refunds'),
    dataIndex: 'revenueUsd',
    align: 'right',
    render: (v) => fmtUsd(v),
  },
  {
    title: 'Purchases (backend)',
    dataIndex: 'backendPurchases',
    align: 'right',
    render: (v) => fmtNum(v),
  },
  {
    title: 'Revenue (backend, INR)',
    dataIndex: 'backendRevenueInr',
    align: 'right',
    render: (v) => inr(v),
  },
  {
    title: 'Revenue (backend, USD)',
    dataIndex: 'backendRevenueUsd',
    align: 'right',
    render: (v) => fmtUsd(v),
  },
  { title: 'Refunds', dataIndex: 'refunds', align: 'right', render: (v) => fmtNum(v) },
  { title: 'Refunds (USD)', dataIndex: 'refundsUsd', align: 'right', render: (v) => fmtUsd(v) },
  {
    title: titleWithInfo(
      'Backend coverage',
      'Days of the range with backend columns. A backend sum covers only those days; the GA4 columns cover all days with a row.'
    ),
    render: (_, r) => <BackendDays backendDays={r.backendDays} days={r.days} />,
  },
];

const Content: React.FC<{ rows: SkuRow[] }> = ({ rows }) => {
  const chart = useMemo(
    () =>
      rows.flatMap((r) => [
        ...(r.revenueUsd !== null
          ? [{ sku: r.productId, series: 'GA4 (gross)', value: r.revenueUsd }]
          : []),
        ...(r.backendRevenueUsd !== null
          ? [{ sku: r.productId, series: 'Backend', value: r.backendRevenueUsd }]
          : []),
      ]),
    [rows]
  );
  if (rows.length === 0) {
    return (
      <Empty description="No SKU activity in this range" image={Empty.PRESENTED_IMAGE_SIMPLE} />
    );
  }
  return (
    <div>
      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
        One row per SKU over the whole range. No player counts here: players are not additive across
        days or SKUs.
      </Text>
      <Card size="small" title="Revenue by SKU (USD)" style={{ marginBottom: 12 }}>
        <div data-chart-wrap="sku-revenue">
          <Column
            data={chart}
            xField="sku"
            yField="value"
            colorField="series"
            group
            height={240}
            legend={{ color: { position: 'top' as const } }}
            axis={{ y: { labelFormatter: (v: number) => fmtUsd(Number(v)) } }}
            animate={false}
          />
        </div>
      </Card>
      <Table<SkuRow>
        size="small"
        rowKey="productId"
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 'max-content' }}
        summary={() =>
          totalRow([
            null,
            fmtNum(sumOf(rows, (r) => r.carts)),
            fmtNum(sumOf(rows, (r) => r.iapDetails)),
            fmtNum(sumOf(rows, (r) => r.purchases)),
            fmtPct(
              rate(
                sumOf(rows, (r) => r.purchases),
                sumOf(rows, (r) => r.carts)
              )
            ),
            fmtNum(sumOf(rows, (r) => r.cancels)),
            fmtNum(sumOf(rows, (r) => r.failed)),
            fmtUsd(sumOf(rows, (r) => r.revenueUsd)),
            fmtNum(sumOf(rows, (r) => r.backendPurchases)),
            inr(sumOf(rows, (r) => r.backendRevenueInr)),
            fmtUsd(sumOf(rows, (r) => r.backendRevenueUsd)),
            fmtNum(sumOf(rows, (r) => r.refunds)),
            fmtUsd(sumOf(rows, (r) => r.refundsUsd)),
            null,
          ])
        }
      />
    </div>
  );
};

interface Props {
  entry: TypedEntry<SkuRow[]>;
  onRetry?: () => void;
}

/** Store funnel and revenue per SKU. */
export const SkusTab: React.FC<Props> = ({ entry, onRetry }) => (
  <TabBody entry={entry} onRetry={onRetry}>
    {(rows) => <Content rows={rows} />}
  </TabBody>
);
